import capitalize from 'lodash/capitalize';
import {memo, useCallback, useState} from 'react';
import {useSelector} from 'react-redux';
import {useHistory, useParams} from 'react-router-dom';
import {selectIsHost} from '~/common/fabric/User/reducers/user';
import {isDateTimeInPast} from '~/common/utils/time';
import {useResendInvitationContext} from '~/overview/contexts/ResendInvitationContext';
import {AvatarImg, Badge} from '~/ViewInvitation/components/Guestlist/Avatar';
import type {GuestlistRouteParams} from '~/ViewInvitation/components/Guestlist/GuestlistOverlay';
import {GuestlistOverlayHeader} from '~/ViewInvitation/components/Guestlist/GuestlistOverlayHeader';
import {generateHeadcountString} from '~/ViewInvitation/components/Guestlist/helpers/utils';
import {useGuestlistDataContext} from '~/ViewInvitation/components/Guestlist/hooks/useGuestlistDataContext';
import {DeleteGuestConfirmModal} from '~/ViewInvitation/components/Guestlist/modals/DeleteConfirmModals';
import {Post} from '~/ViewInvitation/components/Guestlist/Post/Post';
import {
  selectEventEnd,
  selectEventId,
  selectEventStart,
} from '~/ViewInvitation/selectors/eventDetails';
import {selectOptions} from '~/ViewInvitation/selectors/eviteEvent';
import {DeliveryStatus, type Guest, GuestType, isHostGuest} from '~/ViewInvitation/types/guests';
import {Button} from '~library/atoms/Button';
import {GenericSpinner} from '~library/atoms/GenericSpinner';
import {Typography} from '~library/atoms/Typography';
import {ConversationIcon} from '~library/svg/v2/ConversationIcon';
import {EditLineIcon} from '~library/svg/v2/EditLineIcon';
import {ResendIcon} from '~library/svg/v2/ResendIcon';
import {TrashIcon} from '~library/svg/v2/TrashIcon';

import * as styles from './GuestSummary.module.scss';

const getFriendlyDeliveryStatus = (deliveryStatus: DeliveryStatus) => {
  if (deliveryStatus === DeliveryStatus.FAILED) {
    return 'Not Delivered';
  }
  return capitalize(deliveryStatus);
};

interface GuestSummaryProps {
  onClose: () => void;
}

export const GuestSummary = memo(({onClose}: GuestSummaryProps) => {
  const history = useHistory();
  const eventId = useSelector(selectEventId);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const isHost = useSelector(selectIsHost);
  const familyHeadcountEnabled = useSelector(selectOptions)?.event_option_allow_kids;
  const start = useSelector(selectEventStart);
  const end = useSelector(selectEventEnd);
  const isEventPast = isDateTimeInPast(end || start);
  const {userId} = useParams<GuestlistRouteParams>();
  const {
    state: {guestlist},
  } = useGuestlistDataContext();
  const guest = guestlist?.find((g: Guest) => g.userId === userId);
  const hasReplied = ['yes', 'no', 'maybe'].includes(guest?.rsvpResponse || '');
  const canDeleteGuest = guest?.guestType !== GuestType.HOST;
  const {resendInvitation, isResending, resendingId, canResendToGuest} =
    useResendInvitationContext();
  const isResendDisabled =
    (isResending && resendingId === guest?.userId) || !canResendToGuest(guest?.userId!);

  const goBack = useCallback(() => {
    history.replace({
      pathname: `/guests`,
      search: history.location.search,
    });
  }, [history]);

  const doUpdateRsvp = useCallback(() => {
    history.push({pathname: `/guests/${userId}/update-rsvp`, search: history.location.search});
  }, [history, userId]);

  const doResendInvitation = useCallback(async () => {
    if (guest) {
      await resendInvitation({eventId, id: guest.userId, name: guest.name});
    }
  }, [eventId, guest, resendInvitation]);

  const sendMessage = useCallback(() => {
    const searchParams = new URLSearchParams(history.location.search);
    searchParams.set('userId', userId);
    history.push({pathname: `/messages/new`, search: searchParams.toString()});
  }, [history, userId]);

  const onDeleteGuest = useCallback(
    (e?: Error) => {
      if (!e) {
        goBack();
      }
      setDeleteModalOpen(false);
    },
    [goBack]
  );

  if (!guest) return null;

  return (
    <div className={styles.container}>
      <GuestlistOverlayHeader onClose={onClose} goBack={goBack} />
      <div className={styles.infoBlock}>
        <AvatarImg guest={guest} size={64} />
        <div className={styles.nameBlock}>
          <Typography variant="header5">{guest.name}</Typography>
          {isHost && isHostGuest(guest) && (
            <Typography variant="paragraph2">{guest.email || guest.phone}</Typography>
          )}
          {guest.rsvpResponse === 'undecided' ? (
            isHost && isHostGuest(guest) ? (
              <Typography variant="paragraph2">
                {getFriendlyDeliveryStatus(guest.deliveryStatus)}
              </Typography>
            ) : (
              <Typography variant="paragraph2">No Reply</Typography>
            )
          ) : (
            <div className={styles.headcount}>
              <Badge guest={guest} />
              <Typography variant="paragraph2">
                {generateHeadcountString(guest, familyHeadcountEnabled)}
              </Typography>
            </div>
          )}
        </div>
      </div>
      {isHost && (
        <div className={styles.actions}>
          <div className={styles.actionsBlock}>
            <Button
              className="guest-summary__button"
              data-qa-id={`${userId}-update-rsvp-button`}
              onClick={doUpdateRsvp}
              variant="transparent"
              title="Update RSVP"
            >
              <EditLineIcon ratio={0.5} />
              Update guest
            </Button>
            {!isEventPast && (
              <Button
                className="guest-summary__button"
                data-qa-id={`${userId}-resend-button`}
                onClick={doResendInvitation}
                variant="transparent"
                title={isResendDisabled ? 'Resend already sent' : 'Resend invitation to guest'}
                disabled={isResendDisabled}
                disableProcessing
              >
                {isResending ? (
                  <GenericSpinner size={16} />
                ) : (
                  <ResendIcon ratio={0.5} color={isResendDisabled ? 'coin' : 'charcoal'} />
                )}
                Resend
              </Button>
            )}
            {isHostGuest(guest) && guest.deliveryStatus !== DeliveryStatus.FAILED && (
              <Button
                className="guest-summary__button"
                data-qa-id={`${userId}-send-message-button`}
                onClick={sendMessage}
                variant="transparent"
                title="Message guest"
              >
                <ConversationIcon ratio={0.5} />
                Message
              </Button>
            )}
            {canDeleteGuest && (
              <Button
                className="guest-summary__button"
                data-qa-id={`${userId}-delete-guest-button`}
                onClick={() => setDeleteModalOpen(true)}
                variant="transparent"
                title="Delete guest"
              >
                <TrashIcon ratio={0.5} />
              </Button>
            )}
          </div>
        </div>
      )}
      {hasReplied && <Post guest={guest} />}
      <DeleteGuestConfirmModal
        guest={guest}
        onDelete={onDeleteGuest}
        onCancel={() => setDeleteModalOpen(false)}
        isOpen={deleteModalOpen}
      />
      <div id="guest-summary-bottom"></div>
    </div>
  );
});

GuestSummary.displayName = 'GuestSummary';
